import React from 'react';
import { Link } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { FileText, Book, Download, ExternalLink, ArrowLeft } from 'lucide-react';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { LectureDownloader } from '@/components/LectureDownloader';

// Import shared lecture components
import { 
  KeyTakeaway, 
  LectureHeader,
  LectureSidebar, 
  Slideshow, 
  VideoPlaceholder,
  SlideContent
} from '@/components/lectures';

const EstrogenClinicalApplications: React.FC = () => {


  const resources = [
    {
      title: 'Lecture Notes',
      link: '#',
      icon: <FileText className="h-4 w-4" />,
    },
    {
      title: 'Research References',
      link: '#',
      icon: <Book className="h-4 w-4" />,
    },
    {
      title: 'Clinical Guidelines',
      link: '#',
      icon: <Download className="h-4 w-4" />,
    },
    {
      title: 'External Resources',
      link: '#',
      icon: <ExternalLink className="h-4 w-4" />,
    },
  ];

  // Lecture slides content
  const lectureSlides: SlideContent[] = [
    {
      title: "HRT: Principles, Benefits, and Implementation",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-2">
              Hormone Replacement Therapy (HRT) represents a cornerstone intervention in menopause management. Understanding the scientific principles behind effective implementation is crucial for optimizing outcomes.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title - larger and more prominent */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Estrogen Replacement Therapy: Core Principles</text>
                
                {/* Main diagram: 3 Key Principles - More vertically spaced */}
                <g transform="translate(40, 100)">
                  {/* First principle: Physiologic Dosing - wider with more space */}
                  <rect x="0" y="0" width="220" height="120" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2.5" />
                  <text x="110" y="35" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#0d47a1">Physiologic Dosing</text>
                  <line x1="40" y1="50" x2="180" y2="50" stroke="#0d47a1" strokeWidth="1.5" />
                  <text x="110" y="80" textAnchor="middle" fontSize="16" fill="#333">
                    <tspan x="110" dy="0">Mimic natural hormone</tspan>
                    <tspan x="110" dy="25">patterns</tspan>
                  </text>
                  
                  {/* Second principle: Bioidentical Structure - better positioned */}
                  <rect x="280" y="0" width="220" height="120" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                  <text x="390" y="35" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#1b5e20">Bioidentical Structure</text>
                  <line x1="320" y1="50" x2="460" y2="50" stroke="#1b5e20" strokeWidth="1.5" />
                  <text x="390" y="80" textAnchor="middle" fontSize="16" fill="#333">
                    <tspan x="390" dy="0">Identical to human</tspan>
                    <tspan x="390" dy="25">hormones</tspan>
                  </text>
                  
                  {/* Third principle: Balanced Approach - better positioned */}
                  <rect x="560" y="0" width="220" height="120" rx="10" fill="#fff3e0" stroke="#ff9800" strokeWidth="2.5" />
                  <text x="670" y="35" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#e65100">Balanced Approach</text>
                  <line x1="600" y1="50" x2="740" y2="50" stroke="#e65100" strokeWidth="1.5" />
                  <text x="670" y="80" textAnchor="middle" fontSize="16" fill="#333">
                    <tspan x="670" dy="0">Estrogen/progesterone</tspan>
                    <tspan x="670" dy="25">balance</tspan>
                  </text>
                  
                  {/* Connecting arrows - more clearly positioned */}
                  <path d="M110,130 L110,160 L390,160 L390,130" fill="none" stroke="#666" strokeWidth="2" strokeDasharray="5,5" />
                  <path d="M670,130 L670,160 L390,160" fill="none" stroke="#666" strokeWidth="2" strokeDasharray="5,5" />
                  
                  {/* Clinical Implementation Box - significantly more spacious */}
                  <rect x="145" y="180" width="470" height="200" rx="10" fill="white" stroke="#333" strokeWidth="2.5" />
                  <text x="380" y="210" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333">Clinical Implementation</text>
                  <line x1="185" y1="225" x2="575" y2="225" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Implementation details - much more spaced */}
                  <text x="185" y="260" fontSize="18" fontWeight="bold" fill="#333">1. Individualized Assessment</text>
                  <text x="205" y="290" fontSize="16" fill="#333">• Baseline hormonal status and symptoms</text>
                  <text x="205" y="320" fontSize="16" fill="#333">• Risk factors and genetic considerations</text>
                  
                  <text x="185" y="360" fontSize="18" fontWeight="bold" fill="#333">2. Route Selection Hierarchy</text>
                  <text x="205" y="390" fontSize="16" fill="#333">• Transdermal → oral → sublingual</text>
                </g>
                
                {/* Benefits table - completely redesigned for clarity */}
                <g transform="translate(40, 480)">
                  <rect x="0" y="0" width="720" height="80" rx="10" fill="white" stroke="#333" strokeWidth="2" />
                  <text x="360" y="25" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Primary Benefits of Optimized Implementation</text>
                  <line x1="40" y1="35" x2="680" y2="35" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Table content - placed in one row with more space */}
                  <g transform="translate(30, 55)">
                    <text x="90" y="0" textAnchor="middle" fontSize="16" fill="#333">
                      <tspan x="85" dy="0" fontWeight="bold" fill="#0d47a1">Metabolic:</tspan>
                      <tspan dx="5">Insulin sensitivity</tspan>
                    </text>
                    
                    <text x="300" y="0" textAnchor="middle" fontSize="16" fill="#333">
                      <tspan x="295" dy="0" fontWeight="bold" fill="#1b5e20">Cardiovascular:</tspan>
                      <tspan dx="5">Endothelial function</tspan>
                    </text>
                    
                    <text x="530" y="0" textAnchor="middle" fontSize="16" fill="#333">
                      <tspan x="525" dy="0" fontWeight="bold" fill="#e65100">Neurological:</tspan>
                      <tspan dx="5">Cognitive health</tspan>
                    </text>
                  </g>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Effective estrogen replacement therapy follows three core principles: physiologic dosing to match the body's natural patterns, bioidentical hormone structures that properly interact with receptors, and a balanced approach that considers the entire hormonal ecosystem. Individualization is essential, beginning with comprehensive assessment and careful route selection considering first-pass metabolism. When properly implemented, HRT provides metabolic, cardiovascular, neurological, and musculoskeletal benefits while minimizing risks.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Estrogen Formulations: Bioidentical vs. Synthetic",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-2">
              Understanding the fundamental differences between bioidentical and synthetic estrogen formulations is critical for informed clinical decision-making and optimal patient outcomes.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#333">Estrogen Formulations: Bioidentical vs. Synthetic</text>
                
                {/* Main comparison sections - more spaced out */}
                <g transform="translate(20, 90)">
                  {/* Bioidentical section - made wider */}
                  <rect x="0" y="0" width="360" height="380" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2" />
                  <text x="180" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#1b5e20">Bioidentical Estrogens</text>
                  <line x1="30" y1="55" x2="330" y2="55" stroke="#1b5e20" strokeWidth="1" />
                  
                  {/* Bioidentical molecules - more spaced vertically */}
                  <text x="30" y="85" fontSize="17" fontWeight="bold" fill="#333">Key Molecules:</text>
                  <text x="50" y="115" fontSize="15" fill="#333">• 17β-estradiol (E2)</text>
                  <text x="50" y="140" fontSize="15" fill="#333">• Estrone (E1)</text>
                  <text x="50" y="165" fontSize="15" fill="#333">• Estriol (E3)</text>
                  
                  {/* Molecular structure - simplified visualization */}
                  <text x="180" y="195" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Molecular Structure (17β-estradiol)</text>
                  <circle cx="180" cy="230" r="35" fill="none" stroke="#1b5e20" strokeWidth="1.5" />
                  <path d="M 155 220 L 205 220 M 180 195 L 180 220 M 155 240 L 205 240 M 165 250 L 195 250" 
                        stroke="#1b5e20" strokeWidth="1.5" fill="none" />
                  <text x="180" y="275" textAnchor="middle" fontSize="14" fill="#333">Identical to endogenous human estrogens</text>
                  
                  {/* Features - more spaced */}
                  <text x="30" y="305" fontSize="17" fontWeight="bold" fill="#333">Clinical Properties:</text>
                  <text x="50" y="330" fontSize="15" fill="#333">• Precise receptor binding profile</text>
                  <text x="50" y="350" fontSize="15" fill="#333">• Predictable metabolism</text>
                </g>
                
                {/* Synthetic section - moved further right */}
                <g transform="translate(420, 90)">
                  <rect x="0" y="0" width="360" height="380" rx="10" fill="#ffebee" stroke="#f44336" strokeWidth="2" />
                  <text x="180" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#b71c1c">Synthetic Estrogens</text>
                  <line x1="30" y1="55" x2="330" y2="55" stroke="#b71c1c" strokeWidth="1" />
                  
                  {/* Synthetic molecules - more spaced */}
                  <text x="30" y="85" fontSize="17" fontWeight="bold" fill="#333">Key Molecules:</text>
                  <text x="50" y="115" fontSize="15" fill="#333">• Ethinylestradiol</text>
                  <text x="50" y="140" fontSize="15" fill="#333">• Conjugated equine estrogens (CEE)</text>
                  <text x="50" y="165" fontSize="15" fill="#333">• Diethylstilbestrol (historic)</text>
                  
                  {/* Molecular structure - simplified visualization */}
                  <text x="180" y="195" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">Modified Molecular Structure</text>
                  <circle cx="180" cy="230" r="35" fill="none" stroke="#b71c1c" strokeWidth="1.5" />
                  <path d="M 155 220 L 205 220 M 180 195 L 180 240 M 155 240 L 205 240 M 155 210 L 165 210" 
                        stroke="#b71c1c" strokeWidth="1.5" fill="none" />
                  <text x="180" y="275" textAnchor="middle" fontSize="14" fill="#333">Modified structure with added chemical groups</text>
                  
                  {/* Features - more spaced */}
                  <text x="30" y="305" fontSize="17" fontWeight="bold" fill="#333">Clinical Properties:</text>
                  <text x="50" y="330" fontSize="15" fill="#333">• Altered receptor binding ratios</text>
                  <text x="50" y="350" fontSize="15" fill="#333">• Enhanced potency & thrombotic risk</text>
                </g>
                
                {/* Comparison table - more spaced and moved down */}
                <g transform="translate(80, 490)">
                  <text x="320" y="0" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Clinical Comparison Summary</text>
                  
                  <line x1="0" y1="20" x2="640" y2="20" stroke="#333" strokeWidth="1.5" />
                  <text x="150" y="45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1b5e20">Bioidentical</text>
                  <text x="490" y="45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#b71c1c">Synthetic</text>
                  <line x1="0" y1="60" x2="640" y2="60" stroke="#333" strokeWidth="1.5" />
                  
                  <text x="150" y="85" textAnchor="middle" fontSize="15" fill="#333">Lower thrombosis risk with transdermal</text>
                  <text x="490" y="85" textAnchor="middle" fontSize="15" fill="#333">Higher thrombosis risk, especially oral</text>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Bioidentical estrogens (17β-estradiol, estrone, and estriol) are structurally identical to hormones produced by the human body, offering precise receptor interactions and predictable metabolic pathways. In contrast, synthetic estrogens like ethinylestradiol and conjugated equine estrogens contain modified molecular structures that alter receptor binding, extend half-life, and increase potency. These structural differences translate to clinical implications: bioidentical formulations, especially when administered transdermally, generally present lower thrombotic risk profiles while still effectively addressing estrogen deficiency symptoms.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Risk-Benefit Analysis: The Timing Hypothesis",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-2">
              The timing hypothesis is a fundamental concept in estrogen therapy that significantly impacts clinical outcomes, explaining why estrogen's effects may be beneficial or harmful depending on when therapy is initiated.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#333">The Timing Hypothesis: Critical Window for Intervention</text>
                
                {/* Timeline base - more spaced */}
                <line x1="100" y1="200" x2="700" y2="200" stroke="#666" strokeWidth="3" />
                <line x1="100" y1="193" x2="100" y2="207" stroke="#666" strokeWidth="3" />
                <line x1="700" y1="193" x2="700" y2="207" stroke="#666" strokeWidth="3" />
                
                {/* Timeline labels - larger font */}
                <text x="100" y="230" textAnchor="middle" fontSize="16" fill="#333">Menopause</text>
                <text x="400" y="230" textAnchor="middle" fontSize="16" fill="#333">5-10 Years Post-Menopause</text>
                <text x="700" y="230" textAnchor="middle" fontSize="16" fill="#333">Late Post-Menopause</text>
                
                {/* Critical Window Highlight - more visible */}
                <rect x="100" y="140" width="270" height="60" fill="#e3f2fd" fillOpacity="0.7" stroke="#2196f3" strokeWidth="2" strokeDasharray="5,3" />
                <text x="235" y="125" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#0d47a1">Critical Window for Intervention</text>
                
                {/* Intervention outcomes - more spaced */}
                {/* Cardiovascular - spaced out boxes */}
                <g transform="translate(0, 270)">
                  <text x="250" y="0" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Cardiovascular Effects</text>
                  
                  <rect x="80" y="25" width="140" height="45" rx="5" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2" />
                  <text x="150" y="52" textAnchor="middle" fontSize="16" fill="#1b5e20">Protective</text>
                  
                  <rect x="250" y="25" width="140" height="45" rx="5" fill="#fff9c4" stroke="#fbc02d" strokeWidth="2" />
                  <text x="320" y="52" textAnchor="middle" fontSize="16" fill="#f57f17">Neutral</text>
                  
                  <rect x="420" y="25" width="140" height="45" rx="5" fill="#ffebee" stroke="#f44336" strokeWidth="2" />
                  <text x="490" y="52" textAnchor="middle" fontSize="16" fill="#b71c1c">Potentially Harmful</text>
                  
                  <line x1="80" y1="90" x2="200" y2="90" stroke="#4caf50" strokeWidth="3" />
                  <line x1="200" y1="90" x2="400" y2="90" stroke="#fbc02d" strokeWidth="3" />
                  <line x1="400" y1="90" x2="560" y2="90" stroke="#f44336" strokeWidth="3" />
                </g>
                
                {/* Cognitive - better spacing, aligned with cardiovascular section */}
                <g transform="translate(0, 380)">
                  <text x="250" y="0" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Cognitive Effects</text>
                  
                  <rect x="80" y="25" width="150" height="45" rx="5" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2" />
                  <text x="155" y="52" textAnchor="middle" fontSize="16" fill="#1b5e20">Potentially Protective</text>
                  
                  <rect x="260" y="25" width="150" height="45" rx="5" fill="#fff9c4" stroke="#fbc02d" strokeWidth="2" />
                  <text x="335" y="52" textAnchor="middle" fontSize="16" fill="#f57f17">Diminishing Benefit</text>
                  
                  <rect x="440" y="25" width="150" height="45" rx="5" fill="#ffebee" stroke="#f44336" strokeWidth="2" />
                  <text x="515" y="52" textAnchor="middle" fontSize="16" fill="#b71c1c">Neutral/Minimal</text>
                  
                  <line x1="80" y1="90" x2="220" y2="90" stroke="#4caf50" strokeWidth="3" />
                  <line x1="220" y1="90" x2="400" y2="90" stroke="#fbc02d" strokeWidth="3" />
                  <line x1="400" y1="90" x2="590" y2="90" stroke="#f44336" strokeWidth="3" />
                </g>
                
                {/* Molecular basis explanation - more spacious and readable */}
                <rect x="80" y="480" width="640" height="70" rx="10" fill="white" stroke="#333" strokeWidth="1.5" />
                <text x="110" y="505" fontSize="17" fontWeight="bold" fill="#333">Molecular Basis of the Timing Effect:</text>
                <text x="110" y="535" fontSize="15" fill="#333">• Early intervention: Maintained endothelial ERα expression and NO signaling</text>
                <text x="110" y="553" fontSize="15" fill="#333">• Late intervention: Altered receptor expression and inflammatory polarization</text>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            The timing hypothesis represents a paradigm shift in understanding estrogen therapy's effects. When initiated within the "critical window" (typically within 5-10 years of menopause), estrogen therapy offers cardiovascular protection and cognitive benefits. However, these benefits diminish and may reverse with delayed initiation. This time-dependent response has a molecular basis: early intervention preserves endothelial estrogen receptor expression and nitric oxide signaling pathways, whereas late intervention encounters altered receptor profiles and established inflammatory processes. This concept explains discrepancies in clinical trial outcomes and underscores the importance of early intervention when indicated.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Optimizing Metabolic Health with Estrogen Therapy",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-2">
              Estrogen's profound effects on metabolic health extend far beyond reproductive function, offering significant clinical applications for cardiometabolic protection and metabolic syndrome prevention.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#333">Estrogen's Metabolic Mechanisms and Clinical Applications</text>
                
                {/* Main diagram: Central hub with connecting mechanisms - more spaced out */}
                <g transform="translate(400, 220)">
                  {/* Central Estrogen Hub - larger and more prominent */}
                  <circle cx="0" cy="0" r="85" fill="#e1f5fe" stroke="#0288d1" strokeWidth="2.5" />
                  <text x="0" y="-15" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#01579b">Estrogen</text>
                  <text x="0" y="15" textAnchor="middle" fontSize="16" fill="#333">Metabolic</text>
                  <text x="0" y="40" textAnchor="middle" fontSize="16" fill="#333">Regulation</text>
                  
                  {/* Adipose Tissue Effects - moved further for better spacing */}
                  <g transform="translate(-220, -130)">
                    <rect x="-110" y="-45" width="220" height="90" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2" />
                    <text x="0" y="-18" textAnchor="middle" fontSize="17" fontWeight="bold" fill="#1b5e20">Adipose Tissue</text>
                    <text x="0" y="12" textAnchor="middle" fontSize="15" fill="#333">• Reduces visceral fat accumulation</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="15" fill="#333">• Promotes subcutaneous distribution</text>
                    
                    {/* Connection line - adjusted for new position */}
                    <path d="M 30,45 L 130,100 L 220,130" fill="none" stroke="#4caf50" strokeWidth="2" strokeDasharray="5,3" />
                  </g>
                  
                  {/* Insulin Sensitivity - moved further for better spacing */}
                  <g transform="translate(220, -130)">
                    <rect x="-110" y="-45" width="220" height="90" rx="10" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2" />
                    <text x="0" y="-18" textAnchor="middle" fontSize="17" fontWeight="bold" fill="#0d47a1">Insulin Sensitivity</text>
                    <text x="0" y="12" textAnchor="middle" fontSize="15" fill="#333">• Enhances glucose uptake</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="15" fill="#333">• Improves insulin receptor signaling</text>
                    
                    {/* Connection line - adjusted for new position */}
                    <path d="M -30,45 L -130,100 L -220,130" fill="none" stroke="#2196f3" strokeWidth="2" strokeDasharray="5,3" />
                  </g>
                  
                  {/* Inflammation - moved further for better spacing */}
                  <g transform="translate(-220, 130)">
                    <rect x="-110" y="-45" width="220" height="90" rx="10" fill="#fff3e0" stroke="#ff9800" strokeWidth="2" />
                    <text x="0" y="-18" textAnchor="middle" fontSize="17" fontWeight="bold" fill="#e65100">Anti-Inflammatory</text>
                    <text x="0" y="12" textAnchor="middle" fontSize="15" fill="#333">• Reduces pro-inflammatory cytokines</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="15" fill="#333">• Inhibits NF-κB signaling</text>
                    
                    {/* Connection line - adjusted for new position */}
                    <path d="M 30,-45 L 130,-100 L 220,-130" fill="none" stroke="#ff9800" strokeWidth="2" strokeDasharray="5,3" />
                  </g>
                  
                  {/* Lipid Metabolism - moved further for better spacing */}
                  <g transform="translate(220, 130)">
                    <rect x="-110" y="-45" width="220" height="90" rx="10" fill="#f3e5f5" stroke="#9c27b0" strokeWidth="2" />
                    <text x="0" y="-18" textAnchor="middle" fontSize="17" fontWeight="bold" fill="#4a148c">Lipid Metabolism</text>
                    <text x="0" y="12" textAnchor="middle" fontSize="15" fill="#333">• Increases HDL, decreases LDL</text>
                    <text x="0" y="35" textAnchor="middle" fontSize="15" fill="#333">• Modulates hepatic lipid production</text>
                    
                    {/* Connection line - adjusted for new position */}
                    <path d="M -30,-45 L -130,-100 L -220,-130" fill="none" stroke="#9c27b0" strokeWidth="2" strokeDasharray="5,3" />
                  </g>
                </g>
                
                {/* Clinical Applications Box - moved down slightly */}
                <rect x="50" y="450" width="700" height="90" rx="10" fill="white" stroke="#333" strokeWidth="2" />
                <text x="400" y="475" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Clinical Applications in Metabolic Health</text>
                <line x1="100" y1="485" x2="700" y2="485" stroke="#333" strokeWidth="1.5" />
                
                {/* Clinical Applications - more spaced */}
                <g transform="translate(150, 505)">
                  <circle cx="0" cy="0" r="10" fill="#4caf50" />
                  <text x="20" y="5" fontSize="15" fill="#333">Targeted intervention for metabolic syndrome prevention</text>
                </g>
                <g transform="translate(150, 530)">
                  <circle cx="0" cy="0" r="10" fill="#2196f3" />
                  <text x="20" y="5" fontSize="15" fill="#333">Transdermal estradiol for insulin resistance or diabetes</text>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Estrogen exerts multifaceted control over metabolic health through four key mechanisms: adipose tissue distribution, insulin sensitivity regulation, anti-inflammatory effects, and lipid metabolism modulation. Clinically, these mechanisms translate to significant metabolic advantages when appropriately leveraged. Targeted estrogen therapy can help prevent the metabolic syndrome commonly associated with menopause by maintaining favorable adipose distribution and insulin sensitivity. For patients with existing insulin resistance or diabetes, transdermal estradiol formulations are preferred as they avoid first-pass hepatic metabolism, minimizing the impact on glucose regulation while still delivering metabolic benefits.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Cognitive Health: Optimizing Estrogen's Neuroprotective Effects",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-2">
              Estrogen's neuroprotective capabilities offer considerable clinical potential for cognitive health optimization when properly implemented, with specific timing and formulation considerations.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#333">Estrogen's Neuroprotective Mechanisms and Clinical Applications</text>
                
                {/* Brain overview section - larger and more detailed */}
                <g transform="translate(400, 160)">
                  {/* Brain outline - simplified but larger */}
                  <path d="M-120,-70 C-140,-45 -150,-10 -140,25 C-130,60 -95,80 -45,85 C-25,95 25,95 45,85 C95,80 130,60 140,25 C150,-10 140,-45 120,-70 C85,-95 35,-105 0,-100 C-35,-105 -85,-95 -120,-70 Z" 
                    fill="#e8eaf6" stroke="#3949ab" strokeWidth="2.5" />
                  
                  {/* Hippocampus - key region for learning and memory - more prominent */}
                  <ellipse cx="0" cy="20" rx="50" ry="25" fill="#c5cae9" stroke="#3949ab" strokeWidth="2" />
                  <text x="0" y="25" textAnchor="middle" fontSize="15" fontWeight="bold" fill="#283593">Hippocampus</text>
                  
                  {/* Synapse representation - more prominent */}
                  <circle cx="-70" cy="-25" r="20" fill="#bbdefb" stroke="#1976d2" strokeWidth="2" />
                  <text x="-70" y="-60" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#0d47a1">Synaptic</text>
                  <text x="-70" y="-40" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#0d47a1">Plasticity</text>
                  
                  {/* Mitochondria representation - more prominent */}
                  <ellipse cx="70" cy="-25" rx="25" ry="15" fill="#e1bee7" stroke="#7b1fa2" strokeWidth="2" />
                  <text x="70" y="-60" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#4a148c">Mitochondrial</text>
                  <text x="70" y="-40" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#4a148c">Function</text>
                </g>
                
                {/* Mechanisms table - more spacious */}
                <g transform="translate(150, 290)">
                  <rect x="0" y="0" width="500" height="200" rx="10" fill="white" stroke="#333" strokeWidth="2" />
                  <text x="250" y="30" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Key Neuroprotective Mechanisms</text>
                  <line x1="30" y1="45" x2="470" y2="45" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Mechanism 1 - more spaced */}
                  <text x="30" y="75" fontSize="16" fontWeight="bold" fill="#0d47a1">1. Neuroplasticity Enhancement</text>
                  <text x="50" y="100" fontSize="15" fill="#333">• Increases BDNF signaling and dendritic spine density</text>
                  <text x="50" y="125" fontSize="15" fill="#333">• Enhances long-term potentiation (LTP) in hippocampus</text>
                  
                  {/* Mechanism 2 - more spaced */}
                  <text x="30" y="155" fontSize="16" fontWeight="bold" fill="#4a148c">2. Neurodegenerative Pathway Inhibition</text>
                  <text x="50" y="180" fontSize="15" fill="#333">• Reduces amyloid-β accumulation and tau hyperphosphorylation</text>
                  <text x="50" y="205" fontSize="15" fill="#333">• Attenuates microglial-driven neuroinflammation</text>
                </g>
                
                {/* Clinical applications box - more prominent */}
                <rect x="50" y="500" width="700" height="45" rx="8" fill="#ede7f6" stroke="#673ab7" strokeWidth="2" />
                <text x="400" y="528" textAnchor="middle" fontSize="16" fill="#333">
                  <tspan x="400" dy="0" fontWeight="bold">Clinical Application:</tspan>
                  <tspan> Early intervention with transdermal estradiol optimizes neuroprotection</tspan>
                </text>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Estrogen provides neuroprotection through multiple complementary mechanisms: it enhances synaptic plasticity in the hippocampus by increasing BDNF signaling and dendritic spine density, while simultaneously inhibiting neurodegenerative pathways by reducing amyloid-β accumulation and attenuating neuroinflammation. To optimize these cognitive benefits clinically, intervention timing is crucial—treatment should be initiated within the critical window (early menopause) when neuronal estrogen receptors remain responsive. Transdermal estradiol is the preferred formulation as it provides steady hormone levels without first-pass metabolism effects and effectively crosses the blood-brain barrier. This approach not only addresses immediate cognitive symptoms but may contribute to long-term neuroprotection against age-related cognitive decline.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Estrogen for Men: Clinical Applications and Considerations",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-2">
              While traditionally overlooked, estrogen plays crucial roles in male physiology, with emerging clinical applications that require precise understanding of balance and implementation strategies.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#333">Estrogen in Male Health: Physiological Roles and Clinical Applications</text>
                
                {/* Hormone balance section - enlarged and more prominent */}
                <g transform="translate(200, 150)">
                  {/* Scale representation - larger */}
                  <line x1="0" y1="0" x2="400" y2="0" stroke="#333" strokeWidth="3.5" />
                  <path d="M200,-15 L200,25" stroke="#333" strokeWidth="3.5" />
                  
                  {/* Testosterone side - larger */}
                  <g transform="translate(100, 0)">
                    <circle cx="0" cy="0" r="60" fill="#e3f2fd" stroke="#1976d2" strokeWidth="2.5" />
                    <text x="0" y="5" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#0d47a1">Testosterone</text>
                  </g>
                  
                  {/* Estrogen side - larger */}
                  <g transform="translate(300, 0)">
                    <circle cx="0" cy="0" r="60" fill="#fce4ec" stroke="#d81b60" strokeWidth="2.5" />
                    <text x="0" y="5" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#ad1457">Estrogen</text>
                  </g>
                  
                  {/* Balance text - more prominent */}
                  <text x="200" y="-40" textAnchor="middle" fontSize="17" fontWeight="bold" fill="#333">Optimal Hormonal Balance</text>
                </g>
                
                {/* Key physiological roles - more spacious */}
                <g transform="translate(80, 260)">
                  <rect x="0" y="0" width="300" height="200" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                  <text x="150" y="35" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#1b5e20">Physiological Roles in Men</text>
                  <line x1="30" y1="50" x2="270" y2="50" stroke="#1b5e20" strokeWidth="1.5" />
                  
                  <text x="30" y="80" fontSize="16" fontWeight="bold" fill="#333">1. Sexual Function</text>
                  <text x="50" y="105" fontSize="15" fill="#333">• Required for healthy libido</text>
                  <text x="50" y="130" fontSize="15" fill="#333">• Involved in erectile mechanics</text>
                  
                  <text x="30" y="160" fontSize="16" fontWeight="bold" fill="#333">2. Metabolic Regulation</text>
                  <text x="50" y="185" fontSize="15" fill="#333">• Influences insulin sensitivity</text>
                  <text x="50" y="210" fontSize="15" fill="#333">• Modulates fat distribution</text>
                </g>
                
                {/* Clinical considerations - more spacious */}
                <g transform="translate(420, 260)">
                  <rect x="0" y="0" width="300" height="200" rx="10" fill="#fff3e0" stroke="#ff9800" strokeWidth="2.5" />
                  <text x="150" y="35" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#e65100">Clinical Considerations</text>
                  <line x1="30" y1="50" x2="270" y2="50" stroke="#e65100" strokeWidth="1.5" />
                  
                  <text x="30" y="80" fontSize="16" fontWeight="bold" fill="#333">1. Normal Ranges</text>
                  <text x="50" y="105" fontSize="15" fill="#333">• E2: 10-40 pg/mL (optimal)</text>
                  <text x="50" y="130" fontSize="15" fill="#333">• T:E2 ratio more important</text>
                  
                  <text x="30" y="160" fontSize="16" fontWeight="bold" fill="#333">2. Aromatase Regulation</text>
                  <text x="50" y="185" fontSize="15" fill="#333">• Target enzyme rather than</text>
                  <text x="50" y="210" fontSize="15" fill="#333">  direct estrogen manipulation</text>
                </g>
                
                {/* Clinical applications chart - more prominent */}
                <g transform="translate(70, 480)">
                  <rect x="0" y="0" width="660" height="60" rx="10" fill="white" stroke="#333" strokeWidth="2" />
                  <text x="330" y="25" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333">Clinical Applications</text>
                  <line x1="30" y1="35" x2="630" y2="35" stroke="#333" strokeWidth="1.5" />
                  
                  <g transform="translate(330, 50)">
                    <text x="-220" y="0" textAnchor="middle" fontSize="15" fill="#333">• Aromatase modulation for metabolic syndrome</text>
                    <text x="170" y="0" textAnchor="middle" fontSize="15" fill="#333">• Monitoring in androgen replacement therapy</text>
                  </g>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Estrogen plays essential physiological roles in men, including support of sexual function, bone health, and metabolic regulation. The focus for men's health should be on hormonal balance rather than absolute estrogen suppression. Clinically, the testosterone-to-estrogen ratio is often more relevant than isolated estrogen levels, with an optimal E2 range typically between 10-40 pg/mL for adult men. When intervention is necessary, selective aromatase modulation provides a more targeted approach than direct estrogen manipulation, allowing for precision in addressing conditions like gynecomastia or metabolic syndrome. During testosterone replacement therapy, estrogen levels must be carefully monitored, as rising E2 through increased aromatization can negate some benefits of treatment. This nuanced understanding enables clinicians to optimize hormonal health in male patients while avoiding the pitfalls of excessive estrogen suppression.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Women's Estrogen Replacement Protocol: Practical Implementation",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-2">
              Evidence-based protocols for estrogen replacement in women must be tailored to reproductive status and individual needs, with specific considerations for dosing, administration routes, and adjunct therapies.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title - larger and more prominent */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Evidence-Based Estrogen Protocols for Women</text>
                
                {/* Main diagram: Three main categories - more spaced horizontally */}
                <g transform="translate(25, 100)">
                  {/* Premenopausal - wider with more internal spacing */}
                  <rect x="0" y="0" width="240" height="350" rx="10" fill="#e3f2fd" stroke="#1976d2" strokeWidth="2.5" />
                  <text x="120" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#0d47a1">Premenopausal</text>
                  <text x="120" y="60" textAnchor="middle" fontSize="16" fill="#333">(Regular Cycles)</text>
                  <line x1="30" y1="75" x2="210" y2="75" stroke="#0d47a1" strokeWidth="1.5" />
                  
                  {/* Mild-Moderate Symptoms - better spaced */}
                  <text x="30" y="105" fontSize="17" fontWeight="bold" fill="#333">Mild-Moderate Symptoms:</text>
                  <text x="40" y="135" fontSize="15" fill="#333">• Bioidentical estradiol:</text>
                  <text x="50" y="160" fontSize="15" fill="#333">6mg pellet implantation</text>
                  <text x="40" y="190" fontSize="15" fill="#333">• Bi-est (80/20 mix):</text>
                  <text x="50" y="215" fontSize="15" fill="#333">0.25-0.5mg every other day</text>
                  
                  {/* Adjunct Therapies - better spaced */}
                  <text x="30" y="250" fontSize="17" fontWeight="bold" fill="#333">Adjunct Therapies:</text>
                  <text x="40" y="280" fontSize="15" fill="#333">• DHEA: 5-10mg daily</text>
                  <text x="40" y="310" fontSize="15" fill="#333">• B-complex with methylfolate</text>
                </g>
                
                {/* Perimenopausal - positioned better with more space */}
                <g transform="translate(280, 100)">
                  <rect x="0" y="0" width="240" height="350" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                  <text x="120" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#1b5e20">Perimenopausal</text>
                  <text x="120" y="60" textAnchor="middle" fontSize="16" fill="#333">(Irregular Cycles)</text>
                  <line x1="30" y1="75" x2="210" y2="75" stroke="#1b5e20" strokeWidth="1.5" />
                  
                  {/* Standard Protocol - better spaced */}
                  <text x="30" y="105" fontSize="17" fontWeight="bold" fill="#333">Standard Protocol:</text>
                  <text x="40" y="135" fontSize="15" fill="#333">• Bioidentical estradiol:</text>
                  <text x="50" y="160" fontSize="15" fill="#333">6-12.5mg pellet</text>
                  <text x="40" y="190" fontSize="15" fill="#333">• Bi-est: 0.25-1mg daily</text>
                  
                  {/* Progesterone Addition - simplified */}
                  <text x="30" y="225" fontSize="17" fontWeight="bold" fill="#333">Progesterone Addition:</text>
                  <text x="40" y="255" fontSize="15" fill="#333">• Regular periods: 200mg</text>
                  <text x="50" y="280" fontSize="15" fill="#333">oral days 14-28</text>
                  <text x="40" y="310" fontSize="15" fill="#333">• Irregular: 100mg daily</text>
                </g>
                
                {/* Postmenopausal - better positioned and spaced */}
                <g transform="translate(535, 100)">
                  <rect x="0" y="0" width="240" height="350" rx="10" fill="#fff3e0" stroke="#ff9800" strokeWidth="2.5" />
                  <text x="120" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#e65100">Postmenopausal</text>
                  <line x1="30" y1="75" x2="210" y2="75" stroke="#e65100" strokeWidth="1.5" />
                  
                  {/* Standard Replacement - simplified but informative */}
                  <text x="30" y="105" fontSize="17" fontWeight="bold" fill="#333">Standard Replacement:</text>
                  <text x="40" y="135" fontSize="15" fill="#333">• Bioidentical estradiol:</text>
                  <text x="50" y="160" fontSize="15" fill="#333">Thin: 12.5-18mg pellet</text>
                  <text x="50" y="185" fontSize="15" fill="#333">Obese: 6-10mg pellet</text>
                  <text x="40" y="215" fontSize="15" fill="#333">• Bi-est: 1-5mg daily</text>
                  
                  {/* Progesterone Requirement - simplified */}
                  <text x="30" y="250" fontSize="17" fontWeight="bold" fill="#333">Mandatory Addition:</text>
                  <text x="40" y="280" fontSize="15" fill="#333">• Progesterone: 100-200mg</text>
                  <text x="50" y="305" fontSize="15" fill="#333">daily if uterus present</text>
                  <text x="40" y="330" fontSize="15" fill="#333">• DHEA: 5-10mg daily</text>
                </g>
                
                {/* Dosing Principle - more prominent */}
                <g transform="translate(150, 470)">
                  <rect x="0" y="0" width="500" height="60" rx="10" fill="#f3e5f5" stroke="#9c27b0" strokeWidth="2.5" />
                  <text x="250" y="35" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#4a148c">Golden Rule: The heavier the patient, the lower the estrogen dose</text>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Estrogen replacement protocols must be carefully tailored to a woman's reproductive status and individual characteristics. For premenopausal women with symptoms, low-dose bioidentical formulations with bioidentical estradiol (6mg pellet) or Bi-est (0.25-0.5mg every other day) are typically sufficient. Perimenopausal women with irregular cycles require both estrogen and properly timed progesterone supplementation to balance hormonal fluctuations and protect the endometrium. Postmenopausal women generally need higher estrogen doses, with an important counterintuitive dosing principle: heavier patients require lower estrogen doses due to increased peripheral conversion in adipose tissue. Mandatory progesterone (100-200mg daily) must be included for any woman with an intact uterus. Adjunct therapies including methylation support, DHEA, and targeted detoxification support optimize both safety and efficacy of the estrogen protocol.
          </KeyTakeaway>
        </>
      )
    },
    {
      title: "Monitoring, Risk Management, and Specialized Applications",
      content: (
        <>
          <div className="mb-4">
            <p className="mb-2">
              Successful implementation of estrogen protocols requires comprehensive biomarker monitoring, risk management strategies, and specialized approaches for unique clinical scenarios.
            </p>
            <div className="flex justify-center">
              <svg width="800" height="550" viewBox="0 0 800 650" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect x="0" y="0" width="800" height="650" fill="#f9f9f9" />
                
                {/* Title - larger and more prominent */}
                <text x="400" y="50" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#333">Monitoring and Specialized Applications</text>
                
                {/* Laboratory Monitoring Section - expanded and more spacious */}
                <g transform="translate(40, 100)">
                  <rect x="0" y="0" width="340" height="220" rx="10" fill="#e3f2fd" stroke="#1976d2" strokeWidth="2.5" />
                  <text x="170" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#0d47a1">Laboratory Monitoring</text>
                  <line x1="30" y1="50" x2="310" y2="50" stroke="#0d47a1" strokeWidth="1.5" />
                  
                  {/* Women's Parameters - better spaced */}
                  <text x="30" y="80" fontSize="17" fontWeight="bold" fill="#333">Women's Parameters:</text>
                  <text x="50" y="110" fontSize="15" fill="#333">• Estradiol: 50-250 pg/mL</text>
                  <text x="50" y="135" fontSize="15" fill="#333">• Estrone: 30-110 pg/mL</text>
                  <text x="50" y="160" fontSize="15" fill="#333">• Progesterone: 3-20 ng/mL</text>
                  <text x="50" y="185" fontSize="15" fill="#333">• DHEA-S: Mid-reference range</text>
                  
                  {/* Men's Parameters - more clearly separated */}
                  <text x="170" y="220" textAnchor="middle" fontSize="15" fill="#0d47a1">
                    Men: Maintain estradiol 20-42 pg/mL; E2:T ratio {'<'}5%
                  </text>
                </g>
                
                {/* Contraindications Section - expanded and better organized */}
                <g transform="translate(420, 100)">
                  <rect x="0" y="0" width="340" height="220" rx="10" fill="#ffebee" stroke="#f44336" strokeWidth="2.5" />
                  <text x="170" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#b71c1c">Contraindications</text>
                  <line x1="30" y1="50" x2="310" y2="50" stroke="#b71c1c" strokeWidth="1.5" />
                  
                  {/* Absolute Contraindications - cleaner list */}
                  <text x="30" y="80" fontSize="17" fontWeight="bold" fill="#333">Absolute:</text>
                  <text x="50" y="110" fontSize="15" fill="#333">• Active hormone-dependent cancer</text>
                  <text x="50" y="135" fontSize="15" fill="#333">• Active thromboembolic disorders</text>
                  <text x="50" y="160" fontSize="15" fill="#333">• Undiagnosed vaginal bleeding</text>
                  <text x="50" y="185" fontSize="15" fill="#333">• Severe liver disease</text>
                  
                  {/* Relative - simplified to save space */}
                  <text x="230" y="80" fontSize="17" fontWeight="bold" fill="#333">Relative:</text>
                  <text x="250" y="110" fontSize="15" fill="#333">• History of VTE</text>
                  <text x="250" y="135" fontSize="15" fill="#333">• Hypertension</text>
                  <text x="250" y="160" fontSize="15" fill="#333">• Migraine with aura</text>
                  <text x="250" y="185" fontSize="15" fill="#333">• Smoking</text>
                </g>
                
                {/* Specialized Applications - more clearly organized */}
                <g transform="translate(40, 340)">
                  <rect x="0" y="0" width="720" height="190" rx="10" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2.5" />
                  <text x="360" y="35" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#1b5e20">Specialized Applications</text>
                  <line x1="40" y1="50" x2="680" y2="50" stroke="#1b5e20" strokeWidth="1.5" />
                  
                  {/* Three columns of specialized applications - better positioned */}
                  <g transform="translate(0, 65)">
                    {/* Column 1 */}
                    <g transform="translate(120, 0)">
                      <rect x="-70" y="0" width="180" height="110" rx="5" fill="#f1f8e9" stroke="#7cb342" strokeWidth="1" />
                      <text x="20" y="25" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#33691e">Post-Hysterectomy</text>
                      <text x="20" y="55" textAnchor="middle" fontSize="15" fill="#333">• Begin within 4 weeks</text>
                      <text x="20" y="80" textAnchor="middle" fontSize="15" fill="#333">• Higher initial dose</text>
                      <text x="20" y="105" textAnchor="middle" fontSize="15" fill="#333">• Include progesterone</text>
                    </g>
                    
                    {/* Column 2 */}
                    <g transform="translate(360, 0)">
                      <rect x="-70" y="0" width="180" height="110" rx="5" fill="#f1f8e9" stroke="#7cb342" strokeWidth="1" />
                      <text x="20" y="25" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#33691e">Post-Cancer</text>
                      <text x="20" y="55" textAnchor="middle" fontSize="15" fill="#333">• 5+ years post-remission</text>
                      <text x="20" y="80" textAnchor="middle" fontSize="15" fill="#333">• Estriol-only option</text>
                      <text x="20" y="105" textAnchor="middle" fontSize="15" fill="#333">• Oncologist collaboration</text>
                    </g>
                    
                    {/* Column 3 */}
                    <g transform="translate(600, 0)">
                      <rect x="-70" y="0" width="180" height="110" rx="5" fill="#f1f8e9" stroke="#7cb342" strokeWidth="1" />
                      <text x="20" y="25" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#33691e">Transgender Care</text>
                      <text x="20" y="55" textAnchor="middle" fontSize="15" fill="#333">• MTF: 2-4mg daily</text>
                      <text x="20" y="80" textAnchor="middle" fontSize="15" fill="#333">• Target E2: 100-200 pg/mL</text>
                      <text x="20" y="105" textAnchor="middle" fontSize="15" fill="#333">• FTM: Low-dose patches</text>
                    </g>
                  </g>
                </g>
              </svg>
            </div>
          </div>
          <KeyTakeaway>
            Comprehensive monitoring is essential for safe, effective estrogen therapy. For women, target estradiol levels of 50-250 pg/mL and estrone levels of 30-110 pg/mL provide adequate physiological replacement while minimizing risks. For men, estradiol should remain between 20-42 pg/mL, with the E2:T ratio maintained below 5%. Absolute contraindications to estrogen therapy include active hormone-dependent cancer, thromboembolic disorders, and liver disease, while relative contraindications require careful individualized assessment. Specialized protocols are necessary for unique situations: post-hysterectomy patients typically need higher initial doses with more frequent monitoring; patients with hormone-sensitive cancer history may benefit from estriol-only formulations with mandatory DIM supplementation; and transgender care requires specific dosing strategies with careful monitoring of multiple parameters. In all cases, regular assessment of metabolic, cardiovascular, and inflammatory markers optimizes long-term safety.
          </KeyTakeaway>
        </>
      )
    }
  ];

  return (
    <MentorshipAccessGate>
      <Navbar />
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Main content */}
          <div className="md:col-span-3">
            <LectureHeader 
              title="Estrogen: Clinical Applications"
              subtitle="Evidence-based approaches to estrogen therapy, including HRT protocols, risk-benefit analysis, and optimization strategies."
              lastUpdated="April 15, 2025"
            />
            
            {/* Slideshow */}
            <Slideshow slides={lectureSlides} />
            
            {/* Video placeholder */}
            <VideoPlaceholder style="simple" />
          </div>
          
          {/* Sidebar */}
          <div className="md:col-span-1">
            <LectureSidebar resources={resources} />
          </div>
        </div>
      </div>
      <Footer />
    </MentorshipAccessGate>
  );
};

export default EstrogenClinicalApplications;